import type {
  CatalogItemDetail,
  CatalogListPaginationResponse,
  CatalogListResponse,
  CatalogV2DetailResponse,
  CatalogV2ListResponse,
  CatalogV2PaginationResponse,
  CatalogV2VariationsResponse,
  CatalogVariationsResponse,
} from '@skupreme/api-contracts/core';
import {
  activeScopeKey,
  activeScopeParams,
} from '@features/account-view/active';
import { scopeParams } from '@features/account-view/scope';
import type { CatalogMarketsResponse } from '@features/catalog/lib/markets-api';
import type {
  ListingMappingBody,
  ListingMappingResult,
  MappableListingsPage,
  MappableListingsParams,
  MappingItemsParams,
  MappingItemsResponse,
} from '@features/catalog-mapping';
import { CacheUtility } from '@utility/cache';
import { CommonUtility } from '@utility/common';
import { APIPath } from '../constants/api';
import { BaseService } from './base';
import { CRUDService } from './crud';

/* The API answers the catalog paths with the V2 shape for a catalog_page_v2 account. */
export type CatalogListResult = CatalogListResponse | CatalogV2ListResponse;
export type CatalogPageResult =
  | CatalogListPaginationResponse
  | CatalogV2PaginationResponse;
export type CatalogItemResult = CatalogItemDetail | CatalogV2DetailResponse;

export function isCatalogV2<V extends { source: 'v2' }>(
  response: object | undefined,
): response is V {
  return !!response && 'source' in response && response.source === 'v2';
}

/* EN-4396: the V2 list reads carry the active level's `scope` and `division`; at the division level there are none. */
const scoped = (params: object) => ({ ...params, ...activeScopeParams() });

const variationsUrl = (parentId: string) =>
  `${APIPath.catalogVariations}/${parentId}`;

class Catalog extends CRUDService {
  constructor() {
    super(APIPath.catalog);
  }

  getById(id: string): Promise<CatalogItemResult> {
    return super.getById(id);
  }

  mergeListingData = params => {
    const query = CommonUtility.objectToParams(params);
    return BaseService.get(`${APIPath.catalog}/conflict/true?${query}`);
  };

  mergePageData = params => {
    const query = CommonUtility.objectToParams(params);
    return BaseService.get(
      `${APIPath.catalog}/conflict/true/pagination?${query}`,
    );
  };

  conflictAttributesItems = params => {
    const query = CommonUtility.objectToParams(params);
    return BaseService.get(
      `${APIPath.catalog}/conflictAttributesItems?${query}`,
    );
  };

  conflictAttributesPageData = params => {
    const query = CommonUtility.objectToParams(params);
    return BaseService.get(
      `${APIPath.catalog}/conflictAttributesItems/pagination?${query}`,
    );
  };

  getHealth = marketplaceRegionId => {
    const url = `${APIPath.catalog}/${marketplaceRegionId}/health`;
    return BaseService.get(url);
  };

  resolveAttributeConflict = reqData => {
    const url = `${APIPath.resolveAttributeConflict}`;
    return BaseService.post(url, reqData);
  };

  mergedData = params => {
    const query = CommonUtility.objectToParams(scoped(params));
    const url = `${APIPath.catalog}/conflict/false?${query}`;
    return BaseService.get<CatalogListResult>(url);
  };

  pageData = params => {
    const query = CommonUtility.objectToParams(scoped(params));
    const url = `${APIPath.catalog}/conflict/false/pagination?${query}`;
    return BaseService.get<CatalogPageResult>(url);
  };

  /* M2: the countries the list shows, from the list's own query (`listParams`, F1847); `everyDivision` drops the
     division filter and the list's query (for the division labels). */
  markets = (listParams: object = {}, everyDivision = false) => {
    const params = everyDivision
      ? scopeParams(activeScopeKey().level)
      : scoped(listParams);
    const query = CommonUtility.objectToParams(params);
    const url = `${APIPath.catalog}/conflict/false/markets?${query}`;
    return BaseService.get<CatalogMarketsResponse>(url);
  };

  resolveConflict = data => BaseService.post(APIPath.resolveConflict, data);

  updateDescription = reqData =>
    BaseService.put(APIPath.updateItemDescription, reqData);

  ItemSalesData = reqData =>
    BaseService.put(APIPath.catalogPreictions, reqData);

  updateItemInfo = reqData => BaseService.post(APIPath.updateItemInfo, reqData);

  variations = async (
    parentId,
    forceRefresh = false,
  ): Promise<CatalogVariationsResponse> => {
    const url = variationsUrl(parentId);
    const cache = CacheUtility.get(url);
    if (cache && !forceRefresh) {
      return cache;
    }
    const result = await BaseService.get<CatalogVariationsResponse>(url);
    CacheUtility.save(url, result);
    return result;
  };

  /* EN-4396: a V2 parent's variants; keyed by the account, so no scope or division. The query cache is the cache. */
  v2Variations = (parentId: string) =>
    BaseService.get<CatalogV2VariationsResponse>(variationsUrl(parentId));

  confictFile = marketplaceRegionId =>
    BaseService.get(
      `${APIPath.catalog}/${marketplaceRegionId}/conflictAttributesItems/spreadsheet`,
    );

  getCatalogConflictsCount = params => {
    const query = CommonUtility.objectToParams(params);
    return BaseService.get(`${APIPath.catalogConflictsCount}?${query}`);
  };

  getAutocomplete = (params, signal?: AbortSignal) => {
    const query = CommonUtility.objectToParams(params);
    return BaseService.get(
      `${APIPath.catalogAutocomplete}?${query}`,
      false,
      true,
      signal,
    );
  };

  addItem(data) {
    return BaseService.post(APIPath.catalog, data);
  }

  fetchCatalogConflictsList = params => {
    const query = CommonUtility.objectToParams(params);
    return BaseService.get(`${APIPath.catalogConflictsList}?${query}`);
  };

  updateUniqueIdentifier = (itemId, data) =>
    BaseService.put(`${APIPath.catalogUniqueIdentifier}/${itemId}`, data);

  unmatchedItems = reqParams => {
    const params = CommonUtility.objectToParams(reqParams);
    return BaseService.get(`${APIPath.unmatchItems}?${params}`);
  };

  downloadFeedTemplate = reqParams => {
    const params = CommonUtility.objectToParams(reqParams);
    return BaseService.get(`${APIPath.catalogFeedsTemplate}?${params}`);
  };

  getCatalogFeedUpload = (feedType, reqParams) => {
    const params = CommonUtility.objectToParams(reqParams);
    return BaseService.get(
      `${APIPath.catalogFeedsUploadUrl}/${feedType}/?${params}`,
    );
  };

  feedList = filter => {
    const params = CommonUtility.objectToParams(filter);
    return BaseService.get(`${APIPath.catalogFeeds}/?${params}`);
  };

  insertItem = data => {
    return BaseService.put(APIPath.insertMarketplaceItem, data);
  };

  getSCMReportList = params => {
    const query = CommonUtility.objectToParams(params);
    return BaseService.get(`${APIPath.scmInventoryReport}?${query}`);
  };

  fetchSCMReport = data => {
    return BaseService.post(APIPath.scmInventoryReport, data);
  };

  fetchGlobalSaleSpike = id => {
    return BaseService.get(`${APIPath.seasonalSpike}/${id}`);
  };

  getUnmatchedListings = params => {
    const query = CommonUtility.objectToParams(params);
    return BaseService.get(
      `${APIPath.marketplaceUnmatchedListingsFeed}?${query}`,
    );
  };

  downloadUmatchedFeedTemplate = params => {
    const query = CommonUtility.objectToParams(params);
    return BaseService.get(
      `${APIPath.marketplaceUnmatchedListingsTemplate}?${query}`,
    );
  };

  getUnmatchedListingsFeedUploadUrl = params => {
    const query = CommonUtility.objectToParams(params);

    return BaseService.get(
      `${APIPath.marketplaceUnmatchedListingsUploadUrl}?${query}`,
    );
  };

  getMatchedListings = params => {
    const query = CommonUtility.objectToParams(params);
    return BaseService.get(`${APIPath.marketplaceItems}?${query}`);
  };

  /* EN-4496: the mapping tab reads and writes Postgres; the account comes from the session. */
  getMappableListings = (params: MappableListingsParams) =>
    BaseService.get<MappableListingsPage>(
      `${APIPath.catalogMappableListings}?${CommonUtility.objectToParams(params)}`,
    );

  getMappingItems = (params: MappingItemsParams, signal?: AbortSignal) =>
    BaseService.get<MappingItemsResponse>(
      `${APIPath.catalogMappingItems}?${CommonUtility.objectToParams(params)}`,
      false,
      true,
      signal,
    );

  putListingMapping = (listingId: string, body: ListingMappingBody) =>
    BaseService.put<ListingMappingResult>(
      `${APIPath.catalogListings}/${encodeURIComponent(listingId)}/mapping`,
      body,
    );
}

const CatalogService = new Catalog();
Object.freeze(CatalogService);
export default CatalogService;
